import { useEffect, useState } from 'react'
import { useAuth } from '../hooks'

// ── Shared building blocks ───────────────────────────────────────────────────

// Only blue (--brand) and red (--red) are used here — both are already tuned
// per theme in index.css, so text stays properly readable in light mode too
// (unlike a fixed hex tuned only for dark backgrounds).
const CALLOUT_STYLES = {
  info:    { bg: 'color-mix(in srgb, var(--brand) 8%, transparent)', border: 'color-mix(in srgb, var(--brand) 25%, transparent)', color: 'var(--brand)' },
  tip:     { bg: 'color-mix(in srgb, var(--brand) 8%, transparent)', border: 'color-mix(in srgb, var(--brand) 25%, transparent)', color: 'var(--brand)' },
  warn:    { bg: 'color-mix(in srgb, var(--red) 8%, transparent)',   border: 'color-mix(in srgb, var(--red) 25%, transparent)',   color: 'var(--red)' },
  danger:  { bg: 'color-mix(in srgb, var(--red) 8%, transparent)',   border: 'color-mix(in srgb, var(--red) 25%, transparent)',   color: 'var(--red)' },
  lock:    { bg: 'color-mix(in srgb, var(--red) 8%, transparent)',   border: 'color-mix(in srgb, var(--red) 25%, transparent)',   color: 'var(--red)' },
}

const CALLOUT_ICONS = {
  info: (
    <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="8" r="6.5"/><path d="M8 7.3v4"/><circle cx="8" cy="5" r="0.4" fill="currentColor" stroke="none"/>
    </svg>
  ),
  tip: (
    <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 1.5c-2.2 0-4 1.8-4 4 0 1.5.8 2.4 1.5 3.2.4.5.7.9.7 1.3h3.6c0-.4.3-.8.7-1.3.7-.8 1.5-1.7 1.5-3.2 0-2.2-1.8-4-4-4z"/>
      <path d="M6.3 12.5h3.4M6.7 14h2.6"/>
    </svg>
  ),
  warn: (
    <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 1.8 1 13.8h14z"/><path d="M8 6.2v3.4"/><circle cx="8" cy="11.5" r="0.4" fill="currentColor" stroke="none"/>
    </svg>
  ),
  danger: (
    <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 1.8 1 13.8h14z"/><path d="M8 6.2v3.4"/><circle cx="8" cy="11.5" r="0.4" fill="currentColor" stroke="none"/>
    </svg>
  ),
  lock: (
    <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="7" width="10" height="7" rx="1.2"/><path d="M5.2 7V4.8a2.8 2.8 0 0 1 5.6 0V7"/>
    </svg>
  ),
}

function Callout({ type = 'info', title, children }) {
  const s = CALLOUT_STYLES[type]
  return (
    <div className="rounded-xl p-4 my-4" style={{ background: s.bg, border: `1px solid ${s.border}` }}>
      {title && (
        <p className="flex items-center gap-1.5 text-sm font-semibold mb-1" style={{ color: s.color }}>
          <span className="flex-shrink-0">{CALLOUT_ICONS[type]}</span>
          {title}
        </p>
      )}
      <div className="text-sm leading-relaxed" style={{ color: 'var(--text-2)' }}>{children}</div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col sm:flex-row gap-1 sm:gap-4 py-3" style={{ borderBottom: '1px solid var(--border)' }}>
      <div className="text-xs font-bold uppercase tracking-wide flex-shrink-0 sm:w-40" style={{ color: 'var(--brand)' }}>{label}</div>
      <div className="text-sm leading-relaxed" style={{ color: 'var(--text-2)' }}>{children}</div>
    </div>
  )
}

function Step({ n, title, children }) {
  return (
    <div className="flex gap-3 mb-4">
      <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
           style={{ background: 'var(--brand)', color: '#fff' }}>{n}</div>
      <div>
        <p className="text-sm font-semibold mb-0.5" style={{ color: 'var(--text-1)' }}>{title}</p>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--text-2)' }}>{children}</p>
      </div>
    </div>
  )
}

function CardGrid({ children }) {
  return <div className="grid sm:grid-cols-2 gap-4 my-4">{children}</div>
}

function InfoCard({ icon, title, children, list }) {
  return (
    <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      {icon && <p className="text-xl mb-1">{icon}</p>}
      <p className="text-sm font-bold mb-1.5" style={{ color: 'var(--text-1)' }}>{title}</p>
      {children && <p className="text-xs leading-relaxed mb-2" style={{ color: 'var(--text-3)' }}>{children}</p>}
      {list && (
        <ul className="text-xs space-y-1 pl-4" style={{ color: 'var(--text-2)', listStyle: 'disc' }}>
          {list.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      )}
    </div>
  )
}

function BloomGrid() {
  const levels = [
    { n: '01', t: 'Remember',   d: 'Recall facts & terms' },
    { n: '02', t: 'Understand', d: 'Explain ideas' },
    { n: '03', t: 'Apply',      d: 'Use in new situations' },
    { n: '04', t: 'Analyze',    d: 'Break into parts' },
    { n: '05', t: 'Evaluate',   d: 'Justify a decision' },
    { n: '06', t: 'Create',     d: 'Produce new work' },
  ]
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 my-4">
      {levels.map(l => (
        <div key={l.n} className="rounded-lg p-3 text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <p className="text-[0.65rem] font-bold" style={{ color: 'var(--brand)' }}>{l.n}</p>
          <p className="text-xs font-bold my-0.5" style={{ color: 'var(--text-1)' }}>{l.t}</p>
          <p className="text-[0.65rem]" style={{ color: 'var(--text-3)' }}>{l.d}</p>
        </div>
      ))}
    </div>
  )
}

function Section({ id, num, title, badge, children }) {
  return (
    <section id={id} className="mb-10 scroll-mt-20">
      <div className="flex items-baseline gap-3 mb-4">
        <span className="text-2xl font-extrabold" style={{ color: 'var(--brand)' }}>{num}</span>
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-1)' }}>{title}</h2>
        {badge && (
          <span className="text-[0.65rem] font-bold px-2 py-0.5 rounded-full" style={{ background: 'color-mix(in srgb, var(--brand) 15%, transparent)', color: 'var(--brand)' }}>{badge}</span>
        )}
      </div>
      {children}
    </section>
  )
}

function SubHead({ children }) {
  return <h3 className="text-sm font-bold mt-6 mb-2" style={{ color: 'var(--text-1)' }}>{children}</h3>
}

function P({ children }) {
  return <p className="text-sm leading-relaxed mb-3" style={{ color: 'var(--text-2)' }}>{children}</p>
}

function ScrollToTop() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    function onScroll() { setVisible(window.scrollY > 400) }
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  if (!visible) return null

  return (
    <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} title="Back to top"
      className="fixed bottom-6 right-6 w-11 h-11 rounded-full flex items-center justify-center shadow-lg transition-all z-40"
      style={{ background: 'var(--brand)', color: '#fff' }}
      onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
      onMouseLeave={e => e.currentTarget.style.opacity = '1'}
    >
      <svg className="w-5 h-5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8 12.5V3.5M4 7.5 8 3.5l4 4"/>
      </svg>
    </button>
  )
}

// ── Table of contents ─────────────────────────────────────────────────────────

const EXAMINEE_TOC = [
  ['ex-01', '01', 'Getting Started'],
  ['ex-02', '02', 'Your Home Page'],
  ['ex-03', '03', 'Exam Modes'],
  ['ex-04', '04', 'Taking an Exam'],
  ['ex-05', '05', 'How Grading Works'],
  ['ex-06', '06', 'Your Results'],
  ['ex-07', '07', 'Exam History'],
  ['ex-08', '08', 'Your Profile'],
]

const ORG_TOC = [
  ['org-01', '01', 'Getting Started'],
  ['org-02', '02', 'Your Dashboard'],
  ['org-03', '03', 'Managing Exams'],
  ['org-04', '04', "Bloom's Taxonomy & Marks Weighting"],
  ['org-05', '05', 'Trials & Practice Settings'],
  ['org-06', '06', 'Plan Permissions'],
  ['org-07', '07', 'Credits & Billing'],
  ['org-08', '08', 'Managing Examinees'],
  ['org-09', '09', 'Exam History & Results'],
  ['org-10', '10', 'Granting Retakes'],
  ['org-11', '11', 'Your Profile'],
]

function Toc({ items }) {
  return (
    <div className="rounded-xl p-4 mb-8" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <p className="text-[0.65rem] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--text-3)' }}>On this page</p>
      <div className="flex flex-wrap gap-1.5">
        {items.map(([id, num, title]) => (
          <a key={id} href={`#${id}`}
             className="text-xs font-medium px-2.5 py-1 rounded-full transition-colors"
             style={{ background: 'var(--surface-2)', color: 'var(--text-2)', border: '1px solid var(--border)' }}
             onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--brand)'}
             onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
            {num} {title}
          </a>
        ))}
      </div>
    </div>
  )
}

// ── Examinee Guide ────────────────────────────────────────────────────────────

function ExamineeGuide() {
  return (
    <div>
      <Toc items={EXAMINEE_TOC} />

      <Section id="ex-01" num="01" title="Getting Started">
        <SubHead>Creating Your Account</SubHead>
        <P>Navigate to the RADTAR SkillAssess platform in your browser. On the login page, click <b>Create an account</b> to begin registration. You will be asked for your full name, email address, phone number, and a password.</P>
        <P>There are two ways to register, depending on how you are accessing the platform:</P>
        <CardGrid>
          <InfoCard icon="🏢" title="Examinee (Organisation Member)"
            list={[
              'Your invite code comes from your organisation',
              "Don't have it yet? Contact whoever directed you here",
              'Access exams using a separate exam code shared by your organisation',
            ]}>
            A company or institution has enrolled you. During registration, enter the Invite Code your organisation gave you.
          </InfoCard>
          <InfoCard icon="👤" title="Examinee (Standalone)"
            list={[
              'Access mock and trial exams provided by the platform',
              'Trials mode only — Exams mode needs an organisation',
              'Account subject to a validity period',
            ]}>
            No organisation involved — leave the Invite Code field blank on the registration form.
          </InfoCard>
        </CardGrid>
        <Callout type="info" title="Not sure which applies to you?">
          If a company, institution, or exam coordinator directed you here, you're an Examinee (Organisation Member) — ask them for the Invite Code first. Signing up entirely on your own? Leave the code field blank.
        </Callout>
        <Callout type="warn" title="Registered with an Invite Code? You need your organisation's approval first">
          Submitting the registration form doesn't make your account active right away — your organisation has to approve you before you can log in. If your login isn't working right after registering, it likely just means you're still waiting on that approval. Reach out to your organisation if it's taking a while.
        </Callout>

        <SubHead>Logging In</SubHead>
        <Step n={1} title="Go to the platform">Open your browser and navigate to the RADTAR SkillAssess URL provided by your organisation or registration confirmation email.</Step>
        <Step n={2} title="Enter your credentials">Type your registered email address and password. Click Log In to continue.</Step>
        <Step n={3} title="You're in">You'll land on your home page, where your available exams and performance stats are displayed.</Step>
        <Callout type="info" title="Forgot your password?">Use the Forgot password link on the login screen. A reset link is sent to your registered email.</Callout>
      </Section>

      <Section id="ex-02" num="02" title="Your Home Page">
        <P>After logging in, your home page is your central hub — from here you access your available exams and start your assessment.</P>
        <SubHead>Starting an Exam</SubHead>
        <Field label="Organisation Member">Your organisation shares an exam code with you. Enter it in the <b>Enter Exam Code</b> field and click <b>Start Exam</b>.</Field>
        <Field label="Standalone">Mock and trial exams are listed directly on your home page. Click <b>Start Exam</b> on any available exam.</Field>
        <Callout type="tip" title="Exam codes are case-insensitive">A code like NISM2025 works the same whether typed in upper or lower case.</Callout>
      </Section>

      <Section id="ex-03" num="03" title="Exam Modes">
        <P>RADTAR SkillAssess offers two distinct modes, selected on the setup screen before you begin.</P>
        <CardGrid>
          <InfoCard icon="⏱" title="Exams"
            list={[
              "Full timer running — a “Time's Up!” notice appears the moment it ends",
              'Anti-cheat monitoring active',
              "You'll confirm before submitting early",
              'Score recorded in your history',
              'Retake requires organisation approval',
            ]}>
            The real assessment experience — timed, monitored, and graded. Counts toward your official record.
          </InfoCard>
          <InfoCard icon="📖" title="Trials"
            list={[
              'No time pressure',
              'Instant AI feedback on answers',
              'Retake as many times as you like',
              "Results don't affect your official record",
            ]}>
            A low-stakes space to study and build confidence at your own pace.
          </InfoCard>
        </CardGrid>
        <Callout type="info">Examinee (Standalone) users have access to Trials only — the mode selector is hidden, and you're automatically placed in Trials.</Callout>
        <Callout type="warn" title="Some exams require Trials first">An organisation can configure an exam so Trials must be cleared in order — Beginner, then Intermediate, then Advanced, then Expert — before the actual Exam unlocks. If a difficulty level shows a 🔒, pass the one before it to unlock it.</Callout>
      </Section>

      <Section id="ex-04" num="04" title="Taking an Exam">
        <SubHead>The Setup Screen</SubHead>
        <Step n={1} title="Enter your exam code">Organisation members type their exam code on the home page, then click Start Exam. Standalone users skip this — their exams are listed directly.</Step>
        <Step n={2} title="Review the exam details">See the exam name, number of questions, time limit, and topic breakdown before you begin.</Step>
        <Step n={3} title="Select your mode">Choose Exams or Trials via the toggle (Organisation Members only — Standalone users are always in Trials).</Step>
        <Step n={4} title="Begin">Click Start Exam — the timer begins immediately in Exams mode.</Step>

        <Callout type="info" title="What if you see a low-balance or locked message?">
          Trials and Exams run on AI credits. If you're an <b>Standalone</b> user, exams are billed to your own balance — you'll see a Buy Credits button right in the message, so you can top up and continue immediately. If you're an <b>Organisation Member</b>, exams are billed to your organisation's balance instead — you can't buy credits yourself in that case, so contact your organisation to top up.
        </Callout>

        <SubHead>Question Types</SubHead>
        <Field label="MCQ">Select the single best answer from four labeled options. Only one is correct.</Field>
        <Field label="Multi-Select">
          Select <b>all</b> options that apply — more than one may be correct. Proportional credit for correct picks; any wrong pick zeroes the question.
        </Field>
        <Field label="True / False">Determine whether a statement is true or false.</Field>
        <Field label="Short Answer">Type your response — graded by AI against the key points of the model answer.</Field>

        <SubHead>During the Exam</SubHead>
        <Field label="→ Navigating">Use Previous / Next, or jump to any question via the question number panel.</Field>
        <Field label="→ The Timer">When time runs out, a "Time's Up!" notice appears and your exam auto-submits with whatever you've entered.</Field>
        <Field label="→ Submitting">Click Submit Exam — a confirmation dialog shows time remaining and unanswered questions before finalizing.</Field>

        <SubHead>Academic Integrity — Exams Rules</SubHead>
        <Callout type="danger" title="Do not switch tabs or leave the exam window">Switching tabs, minimizing, or navigating away is detected and results in automatic submission.</Callout>
        <Callout type="danger" title="Screen sharing is not permitted">If detected during the exam, your exam is automatically submitted.</Callout>
        <Callout type="tip" title="Trials has no monitoring">These rules apply to Exams mode only — Trials is unrestricted.</Callout>
      </Section>

      <Section id="ex-05" num="05" title="How Grading Works" badge="New">
        <P>MCQ and True/False are graded as simply right or wrong. Multi-Select and Short Answer use partial credit:</P>
        <Field label="Multi-Select">
          Your credit is <b>marks × (correct options picked ÷ total options)</b> — but only if every option you picked was correct. Any wrong pick drops credit to zero.
        </Field>
        <Callout type="tip" title="Worked example">
          A 4-option question worth 4 marks, 2 correct (A and C):
          <ul className="list-disc pl-5 mt-1 space-y-0.5">
            <li>Pick A and C only → both correct, none wrong → <b>full 4 marks</b></li>
            <li>Pick only A → 1 of 2 correct, nothing wrong → 4 × (1÷4) = <b>1 mark</b></li>
            <li>Pick A, C, and B → B is wrong → <b>0 marks</b>, even though A and C were right</li>
          </ul>
        </Callout>
        <Field label="Short Answer">The AI compares your response against a list of expected key points. Your credit is roughly the fraction of key points covered.</Field>
        <Callout type="tip" title="Worked example">
          A Short Answer question worth 3 marks, 3 expected key points. Covering 2 of 3 clearly typically earns around 2 marks (3 × 2÷3) — the Review Answers screen shows exactly which points you covered (✓) and missed (✗).
        </Callout>
      </Section>

      <Section id="ex-06" num="06" title="Your Results">
        <P>After submitting, RADTAR SkillAssess grades your responses — including sending short answers through AI evaluation — before showing results.</P>
        <SubHead>Score Summary</SubHead>
        <Field label="Percentage">Your score as a percentage of total possible marks.</Field>
        <Field label="Pass / Fail">Whether you met the passing threshold (typically 50%) — green PASSED ✓ or red FAILED ✗.</Field>
        <Field label="Topic Breakdown">Accuracy per topic, weakest to strongest, to help focus your study.</Field>
        <Callout type="info" title="Scoring can be marks-weighted">
          Some exams assign each question marks based on its Bloom's Taxonomy level rather than treating every question equally. When enabled, your percentage reflects marks earned out of marks available.
        </Callout>

        <SubHead>Answer Verdict Types</SubHead>
        <Field label="✓ Correct">Your answer fully matched the expected response.</Field>
        <Field label="◑ Partial">Covered some key points but missed others (Short Answer), or a correct subset with no wrong picks (Multi-Select).</Field>
        <Field label="✗ Wrong">Your answer did not match the expected response.</Field>

        <SubHead>Reviewing Your Answers</SubHead>
        <P>Click Review Answers to open a detailed breakdown — Total Questions · Correct · Partial · Wrong.</P>
        <Field label="MCQ & T/F">Your answer is shown; if incorrect, the correct answer and any explanation are shown too.</Field>
        <Field label="Multi-Select">Every option you selected is shown alongside the full set of correct options.</Field>
        <Field label="Short Answer">Your response, the AI's feedback, key points covered (✓) / missed (✗), and the model answer.</Field>
      </Section>

      <Section id="ex-07" num="07" title="Exam History">
        <P>Accessible from the navigation menu at any time — a chronological record of every exam you've submitted.</P>
        <Field label="Exam Name">The name of the exam you completed.</Field>
        <Field label="Date & Time">When the exam was submitted.</Field>
        <Field label="Score">Final percentage score with Pass / Fail.</Field>
        <Field label="Mode">Whether the attempt was an Exam or a Trial.</Field>
        <SubHead>Viewing a Past Result</SubHead>
        <Step n={1} title="Find the attempt">Locate it in your history list, or search by exam name.</Step>
        <Step n={2} title="Open the detail view">Click the eye icon (👁) to open the full answer review.</Step>
        <Step n={3} title="Review your answers">Every question, your answer, verdict, key points, and the model answer.</Step>
        <SubHead>Retaking an Exam</SubHead>
        <P>Exams mode allows one attempt unless a retake is approved by your organisation. Trials has no retake restrictions.</P>
      </Section>

      <Section id="ex-08" num="08" title="Your Profile">
        <P>Access via your name or avatar in the top navigation bar.</P>
        <Field label="Full Name">Your display name shown throughout the platform.</Field>
        <Field label="Phone Number">Your contact number, including country code.</Field>
        <SubHead>Changing Your Password</SubHead>
        <Step n={1} title="Open the password section">Scroll to Change Password on your profile page.</Step>
        <Step n={2} title="Enter your current password">Required to confirm it's really you.</Step>
        <Step n={3} title="Set and confirm the new password">Type it twice to avoid typos.</Step>
        <Step n={4} title="Save">Click Update Password — required on next login.</Step>
        <Callout type="lock" title="Keep your credentials safe">Never share your password. RADTAR SkillAssess staff will never ask for it.</Callout>
      </Section>
    </div>
  )
}

// ── Organisation Guide ────────────────────────────────────────────────────────

function OrgGuide() {
  return (
    <div>
      <Toc items={ORG_TOC} />

      <Section id="org-01" num="01" title="Getting Started">
        <SubHead>Registering Your Organisation</SubHead>
        <P>Select Register as Organisation and provide:</P>
        <Field label="Organisation Name">The official name of your company or institution.</Field>
        <Field label="Email Address">Used to log in and receive platform notifications.</Field>
        <Field label="Password">A secure password for your organisation account.</Field>
        <SubHead>Approval Process</SubHead>
        <Step n={1} title="Submit registration">Your account is created but not yet active.</Step>
        <Step n={2} title="Await approval">A RADTAR SkillAssess Super Admin reviews and approves your organisation — you'll get an email once that happens.</Step>
        <Step n={3} title="Log in and get started">Your organisation dashboard is ready to use.</Step>
        <Callout type="info" title="Approval may take up to 1 business day">Contact support if you haven't heard back after that.</Callout>
      </Section>

      <Section id="org-02" num="02" title="Your Dashboard">
        <P>Your central hub for managing exams, examinees, and results.</P>
        <SubHead>Your Organisation Invite Code</SubHead>
        <P>A unique code every organisation gets — share it with examinees so they can link their accounts to you when registering.</P>
        <Callout type="tip" title="Share the invite code before examinees register">Once someone registers without a code, they can't be linked to your organisation retroactively.</Callout>
        <SubHead>Navigation</SubHead>
        <Field label="Exams">Create and manage your exams. Each has its own exam code you share with examinees.</Field>
        <Field label="Examinees">View all members who joined via your invite code.</Field>
        <Field label="History">View all exam attempts across every exam you've created.</Field>
      </Section>

      <Section id="org-03" num="03" title="Managing Exams">
        <SubHead>Creating an Exam</SubHead>
        <Step n={1} title="Go to the Exams tab">Click + New Exam.</Step>
        <Step n={2} title="Fill in the exam details">Name, description, question settings — count, topics, question types (MCQ, True/False, Multi-Select, Short Answer), difficulty.</Step>
        <Step n={3} title="Optionally enable Bloom's Taxonomy">Distribute questions across cognitive levels and assign marks per level — see the next section.</Step>
        <Step n={4} title="Set validity dates (optional)">Restrict the exam to a Valid From / Valid Till window, or leave blank to keep it always open.</Step>
        <Step n={5} title="Submit and wait for generation">Click Create Exam — AI generates questions, and you'll get an email when it's ready.</Step>

        <SubHead>Question Generation</SubHead>
        <P>RADTAR SkillAssess uses AI to generate questions automatically based on your topics, difficulty, and question types — you don't write them manually.</P>
        <Callout type="info" title="Generation is asynchronous">The exam shows Generating… until all question pools are ready and you've shared the exam code.</Callout>

        <SubHead>Exam Codes</SubHead>
        <Field label="Invite Code">Shared once at registration — links an examinee to your organisation.</Field>
        <Field label="Exam Code">Shared per exam — lets an examinee access and start that specific exam.</Field>
        <Callout type="warn" title="Only share exam codes with intended examinees">Anyone with the code and a linked account can start the exam.</Callout>

        <SubHead>Previewing Your Exams</SubHead>
        <P>Take any of your created exams yourself, as either an Exam or a Trial — the same way your examinees would.</P>
      </Section>

      <Section id="org-04" num="04" title="Bloom's Taxonomy & Marks Weighting" badge="New">
        <SubHead>What Is Bloom's Taxonomy?</SubHead>
        <P>
          Bloom's Taxonomy is a widely used educational framework — first published in 1956 and still standard in curriculum design today —
          that classifies learning objectives into six hierarchical levels of cognitive complexity. Each level builds on the ones below it:
          simply <i>remembering</i> a fact is a lower-order skill than being able to <i>analyze</i> it, and analysis in turn is less demanding
          than being able to <i>evaluate</i> or <i>create</i> something new with that knowledge.
        </P>
        <P>On RADTAR SkillAssess, it's an optional framework you can apply when creating an exam, so your questions deliberately span a range of cognitive skills instead of testing recall alone — and so scoring can reflect how demanding each question actually is.</P>

        <SubHead>The Six Cognitive Levels</SubHead>
        <BloomGrid />

        <SubHead>Enabling It on an Exam</SubHead>
        <Step n={1} title="Turn on Bloom's Taxonomy">On the Create Exam form, toggle it on — a distribution panel lets you set how many questions fall into each of the six levels.</Step>
        <Step n={2} title="Adjust the distribution">A sensible default split is suggested based on your total question count. Adjust as needed — the total must match your question count. Use Reset to Default anytime.</Step>
        <Step n={3} title="Assign marks per level">Set how many marks a question at each level is worth. Higher-order levels (Evaluate, Create) typically carry more marks, reflecting the extra effort they demand.</Step>

        <SubHead>How This Changes Scoring</SubHead>
        <P>Each question's marks value — not a flat one-point count — determines how much it contributes to an examinee's final score. Their percentage reflects marks earned out of marks possible, so a more heavily weighted question counts for more.</P>
        <Callout type="info">If Bloom's Taxonomy is left off, every question is weighted equally and scoring works exactly as before — entirely optional, and it doesn't change existing exams.</Callout>
        <Callout type="tip" title="Marks are only used for scoring, never for generation">Your marks scheme is applied when an attempt is evaluated — it's never sent to the AI during question generation, so it has no influence on question content or difficulty.</Callout>
      </Section>

      <Section id="org-05" num="05" title="Trials & Practice Settings" badge="New">
        <P>Control whether your exam offers a practice mode at all, and how strict it is, right from the Create Exam form.</P>

        <SubHead>Enable Trials</SubHead>
        <P>On by default. Turn this off to make the exam Exam-only — examinees won't see a Trials tab for it at all, and no practice questions are generated for it, saving generation cost for exams where practice isn't needed.</P>

        <SubHead>Allow Skip Trials</SubHead>
        <P>Only shown when Trials are enabled. Controls whether an examinee can jump straight to any difficulty level, or must clear them in order.</P>
        <Field label="On (default)">Beginner, Intermediate, Advanced, and Expert are all open immediately.</Field>
        <Field label="Off">Examinees must pass Beginner before Intermediate unlocks, then Advanced, then Expert — and the actual Exam itself stays locked until all four Trial levels are passed.</Field>
        <Callout type="warn" title="Turning this off raises the bar to reach the real exam">Make sure examinees know they need to clear every Trial level first — this is enforced on both the interface and the server, not just hidden in the UI.</Callout>

        <SubHead>Trial Question Pool Size</SubHead>
        <P>A multiplier (1× to 10×, in steps of 0.5) applied to your question counts when generating each Trial difficulty pool — so retakes reshuffle through a bigger set instead of repeating the same questions. It only affects Trials; the actual exam pool is never scaled.</P>
        <Field label="Example">100 base questions at 3× generates up to 300 questions for each of the four difficulty pools.</Field>
        <Callout type="info" title="Capped at 450 questions per pool">The multiplier's maximum adjusts automatically based on your question count, so you can never pick a combination that would exceed this limit.</Callout>
      </Section>

      <Section id="org-06" num="06" title="Plan Permissions" badge="Updated">
        <P>Not every question type or feature is available to every organisation by default. Access to Multi-Select, Short Answer, Bloom's Taxonomy, and other advanced features unlocks automatically once your organisation's credits balance reaches that feature's unlock threshold — there's no request or manual approval step.</P>
        <SubHead>What's On by Default</SubHead>
        <P>New organisations start with Multiple Choice and True/False enabled — these are always free. Multi-Select, Short Answer, and Bloom's Taxonomy each have their own credits-balance threshold, set by RADTAR SkillAssess, before they unlock.</P>
        <SubHead>Unlocking a Locked Feature</SubHead>
        <P>Trying to enable something your current balance doesn't yet cover shows a dialog explaining it's locked, with a Buy Credits button that takes you straight to the top-up screen (see the next section). Once your balance reaches the threshold, the feature unlocks immediately — no waiting on anyone to approve it.</P>
        <Callout type="lock" title="Locked features are visible, not hidden">You'll still see what a locked feature does — it's simply disabled until your balance reaches its threshold, so you always know exactly what's available and what it would take to unlock it.</Callout>
      </Section>

      <Section id="org-07" num="07" title="Credits & Billing" badge="New">
        <SubHead>How Credits Work</SubHead>
        <P>RADTAR SkillAssess runs on a prepaid credits balance. Every AI-driven action — generating an exam's questions, and AI-grading Short Answer responses — deducts from your organisation's balance based on real usage. Actions that don't call the AI, like creating the exam record itself or browsing history, cost nothing.</P>
        <Callout type="info" title="Your balance is shared across your organisation">Every examinee linked to your organisation draws from your organisation's balance, not their own — their Trials and exam attempts are billed to you.</Callout>

        <SubHead>Buying Credits</SubHead>
        <Step n={1} title="Open Buy Credits">From your Profile page, or from any low-balance prompt that appears elsewhere on the platform, click Buy Credits.</Step>
        <Step n={2} title="Choose a package or a custom amount">Pick one of the listed credit packages, or enter a custom top-up amount (up to ₹10,000) if none of the packages fit what you need.</Step>
        <Step n={3} title="Pay securely via Razorpay">Your credits balance updates immediately once payment is confirmed, and a confirmation email is sent to you.</Step>

        <SubHead>Redeeming a Voucher</SubHead>
        <P>RADTAR SkillAssess occasionally shares voucher codes offering a fixed amount of credits at a discount. Enter it in the voucher field on the Buy Credits screen before choosing a package — a valid code replaces the package grid with the voucher's own fixed offer. Each voucher can only be redeemed once per organisation.</P>

        <SubHead>Low-Balance &amp; Exhausted Notifications</SubHead>
        <P>You'll receive an email when your balance drops to a low threshold, and again if it becomes fully exhausted. New exam creation, question generation, and exam-taking pause once your balance is exhausted, and resume automatically as soon as you buy more credits.</P>

        <SubHead>Transactions &amp; the Monthly Statement</SubHead>
        <P>Your Profile's Transactions tab shows a month-by-month record of your account: every purchase, a running total of AI usage, and — once a month has closed — a downloadable Statement (Starting Balance, Credits Received, Total Usage, Ending Balance, and an itemized list of that month's payments).</P>
        <Callout type="info" title="A Statement is not a bill">It's a running-balance summary for your own records — you've already paid for your credits upfront. It does not request any payment.</Callout>
      </Section>

      <Section id="org-08" num="08" title="Managing Examinees">
        <SubHead>Enrolling Examinees</SubHead>
        <P>Examinees join by entering your Invite Code during their own registration — self-service, you don't add them manually.</P>
        <Step n={1} title="Share your invite code">Send it to each examinee before they register.</Step>
        <Step n={2} title="Examinee registers">Their account is automatically linked to your organisation.</Step>
        <Step n={3} title="They appear in your Examinees tab">Once registered, they're listed there with a Pending status.</Step>

        <SubHead>Approving Examinees</SubHead>
        <P>A newly registered examinee can't log in yet — you need to approve them first. This is separate from your own organisation's approval (done by a RADTAR SkillAssess Super Admin); approving examinees is entirely up to you.</P>
        <Step n={1} title="Find the pending examinee">In the Examinees tab, look for the amber Pending badge in the Status column.</Step>
        <Step n={2} title="Click Approve">Confirm when prompted — the examinee can now log in and access your exams.</Step>
        <Callout type="tip" title="Nothing else to configure">Approval is a one-time gate per examinee — once approved, they stay approved and don't need re-approval for future exams.</Callout>

        <SubHead>Viewing Enrolled Examinees</SubHead>
        <Field label="Name & Email">The examinee's registered details.</Field>
        <Field label="Joined">The date they enrolled.</Field>
        <Field label="Status">Pending (needs your approval) or Approved (can log in and take exams).</Field>
        <Field label="Exams Taken">Total exams submitted.</Field>
        <Field label="Actions">Approve (if pending), view full history, or remove them from your organisation.</Field>
        <Callout type="danger" title="Removing an examinee is permanent">Their account and all exam attempt history are deleted. This cannot be undone.</Callout>
      </Section>

      <Section id="org-09" num="09" title="Exam History & Results">
        <P>A complete view of all exam attempts made by your examinees across every exam you've created.</P>
        <Field label="Examinee">Name and email of who took the exam.</Field>
        <Field label="Exam">The exam attempted.</Field>
        <Field label="Score">Percentage with Pass / Fail.</Field>
        <Field label="Date">When submitted.</Field>
        <Field label="Mode">Exam or Trial.</Field>
        <SubHead>Reviewing an Attempt in Detail</SubHead>
        <P>Click the eye icon (👁) on any row for the full answer review — every question, the examinee's answer, AI verdict, key points covered/missed for Short Answer, and exact options picked for Multi-Select.</P>
        <Callout type="tip" title="Use detailed review before granting a retake or giving targeted feedback.">
        </Callout>
      </Section>

      <Section id="org-10" num="10" title="Granting Retakes">
        <P>By default, each examinee can only take an exam once. Grant a retake from the History tab if needed.</P>
        <Step n={1} title="Go to the History tab">Find the examinee's attempt — search or filter to locate it quickly.</Step>
        <Step n={2} title="Click Grant Retake">Confirm when prompted.</Step>
        <Step n={3} title="Notify the examinee">The exam becomes available again on their home page, using the same exam code.</Step>
        <Callout type="info">A retake grant applies to the examinee's most recent attempt — a new grant is needed for any further attempt.</Callout>
      </Section>

      <Section id="org-11" num="11" title="Your Profile">
        <P>Access via your organisation name or avatar in the navigation bar.</P>
        <Field label="Organisation Name">Displayed across the platform.</Field>
        <Field label="Email Address">Your login email and notification address.</Field>
        <SubHead>Changing Your Password</SubHead>
        <Step n={1} title="Open the password section">Scroll to Change Password.</Step>
        <Step n={2} title="Enter your current password">Confirms it's really you.</Step>
        <Step n={3} title="Set and confirm the new password">Type it twice.</Step>
        <Step n={4} title="Save">Required on next login.</Step>
        <Callout type="lock" title="Keep your credentials secure">Your account controls exam access for every examinee — never share your password.</Callout>
      </Section>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

function GuideVideo({ videoId, title }) {
  return (
    <div className="mb-8">
      <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--text-3)' }}>Video Walkthrough</p>
      <div className="relative w-full rounded-2xl overflow-hidden" style={{ paddingTop: '56.25%', background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
        <iframe
          className="absolute inset-0 w-full h-full"
          src={`https://www.youtube-nocookie.com/embed/${videoId}`}
          title={title}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    </div>
  )
}

export default function GuidePage() {
  const { user } = useAuth()
  // An examinee has no reason to switch to the Organisation Guide from their
  // own Help button — lock them to their own guide, no tab UI at all. Logged-
  // out visitors and organisation accounts keep both tabs (the guide is a
  // public page, useful for deciding which role fits before registering).
  const isExaminee = user?.role === 'user'
  const [tab, setTab] = useState('examinee')

  return (
    <div className="w-full max-w-4xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--brand)' }}>User Guide</p>
        <h1 className="text-2xl sm:text-3xl font-extrabold mb-2" style={{ color: 'var(--text-1)' }}>
          How to use RADTAR SkillAssess
        </h1>
        <p className="text-sm" style={{ color: 'var(--text-3)' }}>
          Everything you need — whether you're sitting an exam or running one.
        </p>
      </div>

      {/* Video Walkthrough hidden for now
      <GuideVideo videoId="3i5_v_sUZ04" title="RADTAR SkillAssess — Platform Walkthrough" />
      */}

      {!isExaminee && (
        <div className="flex items-center gap-1 border-b mb-8" style={{ borderColor: 'var(--border)' }}>
          {[
            { key: 'examinee', label: '🎓 Examinee Guide' },
            { key: 'org',      label: '🏢 Organisation Guide' },
          ].map(({ key, label }) => (
            <button key={key} onClick={() => setTab(key)}
              className="px-4 py-2 text-sm font-medium transition-all relative"
              style={{ color: tab === key ? 'var(--brand)' : 'var(--text-3)' }}>
              {label}
              {tab === key && <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t" style={{ background: 'var(--brand)' }} />}
            </button>
          ))}
        </div>
      )}

      {isExaminee || tab === 'examinee' ? <ExamineeGuide /> : <OrgGuide />}

      <ScrollToTop />
    </div>
  )
}
